import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { SigmaTopbar } from "./SigmaTopbar";
import { SigmaSidebar } from "./SigmaSidebar";
import { menusObjetoNormalize } from "../Seguridad/Objetos/MenusObjetosModel";
import { ENDPOINTS } from "../Seguridad/Utils/Endpoints";
import { buildSidebarTreeForModule } from "./../../utils/menuTree";
import axios from "axios";
import './../../style/Vars.css';
import './../../style/ElementsStyle.css';
import './../../style/ContainersStyles.css';
import { loadAndApplyActiveTheme } from "../Sistema/Utils/ThemeManager";

import { getSessionUserId } from "../../utils/SessionUser";

const STORAGE_EXPANDED = "sk_sidebar_expanded";
const STORAGE_TOP = "sk_sidebar_active_top";
const STORAGE_ACTIVE_ID = "sk_sidebar_active_id";
const STORAGE_ACTIVE_CLAVE = "sk_sidebar_active_clave";

export const SigmaShell = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [idUsuario] = useState(() => getSessionUserId());

  // 1. Estados iniciales con persistencia fuerte para sobrevivir refrescos
  const [activeTop, setActiveTop] = useState(() => sessionStorage.getItem(STORAGE_TOP) || "1");
  const [activeMenuId, setActiveMenuId] = useState(() => sessionStorage.getItem(STORAGE_ACTIVE_ID));
  const [activeLink, setActiveLink] = useState(() => sessionStorage.getItem(STORAGE_ACTIVE_CLAVE) || "");

  const [expandedState, setExpandedState] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_EXPANDED);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [flatMenus, setFlatMenus] = useState([]);
  const [loadingMenus, setLoadingMenus] = useState(false);

  // Guardado automático en storage
  useEffect(() => { sessionStorage.setItem(STORAGE_TOP, activeTop); }, [activeTop]);
  useEffect(() => {
    if (activeMenuId) sessionStorage.setItem(STORAGE_ACTIVE_ID, String(activeMenuId));
    else sessionStorage.removeItem(STORAGE_ACTIVE_ID);
  }, [activeMenuId]);
  useEffect(() => { sessionStorage.setItem(STORAGE_ACTIVE_CLAVE, activeLink); }, [activeLink]);
  useEffect(() => { sessionStorage.setItem(STORAGE_EXPANDED, JSON.stringify(expandedState)); }, [expandedState]);

  useEffect(() => { loadAndApplyActiveTheme(); }, []);

  const goHome = () => { navigate("/menu"); };

  /**
   * 🔹 Sincroniza la ubicación actual con el menú
   * Implementa búsqueda por prefijo más específico (longest match)
   */
  const syncMenuWithLocation = useCallback((menus) => {
    if (!menus || menus.length === 0) return null;

    const pathnameNorm = location.pathname.toLowerCase().replace(/\/$/, "");
    const query = new URLSearchParams(location.search);
    const dynamicPath = query.get("path");

    // Función de normalización idéntica para comparar
    const normalize = (p) => (p || "").replace(/\/+/g, "/").replace(/\/$/, "").replace(/^\//, "").toLowerCase().trim();

    const currentFull = pathnameNorm === "/menu/dynamic" ? normalize(dynamicPath) : normalize(pathnameNorm);

    let bestMatch = null;
    let maxLen = -1;

    for (const node of menus) {
      const nodeClave = String(node.clave || "").trim();
      const nodeRoute = String(node.route || "").toLowerCase().trim();
      const menuPath = normalize(`${nodeRoute}${nodeClave}`);

      // 1. Prioridad: Coincidencia exacta por clave /navigate (para rutas fijas como /perfil)
      if (nodeRoute.includes("/navigate") && nodeClave.startsWith("/")) {
        if (pathnameNorm === nodeClave.toLowerCase()) {
          return { modId: String(node.idModulo), clave: nodeClave, idMenu: node.idMenu };
        }
      }

      // 2. Coincidencia por ruta dinámica o prefijo
      if (currentFull && menuPath) {
        if (currentFull === menuPath) {
          // Coincidencia exacta es inmediata
          return { modId: String(node.idModulo), clave: nodeClave, idMenu: node.idMenu };
        }

        // Búsqueda del prefijo más largo (ej. si estoy en Catalogo/Semestre/Editor y el menú tiene Catalogo/Semestre)
        if (currentFull.startsWith(menuPath) && menuPath.length > 0) {
          if (menuPath.length > maxLen) {
            maxLen = menuPath.length;
            bestMatch = node;
          }
        }
      }
    }

    if (bestMatch) {
      return {
        modId: String(bestMatch.idModulo),
        clave: String(bestMatch.clave || "").trim(),
        idMenu: bestMatch.idMenu
      };
    }

    return null;
  }, [location.pathname, location.search]);

  // 1. Carga de menús: Solo se ejecuta al montar o si cambia el usuario.
  // No debe depender de la ubicación para evitar que parpadee el sidebar al navegar.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        if (!idUsuario) return;
        setLoadingMenus(true);
        const res = await axios.get(ENDPOINTS.menus + "/permitidos", {
          params: { IdUsuario: idUsuario }
        });
        const flat = (res?.data || []).map(menusObjetoNormalize).filter(Boolean);

        if (!cancel) {
          setFlatMenus(flat);
          // Sincronización inicial rápida
          const syncData = syncMenuWithLocation(flat);
          if (syncData) {
            setActiveTop(syncData.modId);
            setActiveLink(syncData.clave);
            setActiveMenuId(syncData.idMenu);
          }
        }
      } catch (err) {
        if (!cancel) setFlatMenus([]);
      } finally {
        if (!cancel) setLoadingMenus(false);
      }
    })();
    return () => { cancel = true; };
  }, [idUsuario]); // 👈 Quitamos syncMenuWithLocation de aquí

  // Sincronización continua cuando cambia la URL
  useEffect(() => {
    if (flatMenus.length === 0) return;
    const syncData = syncMenuWithLocation(flatMenus);
    if (syncData) {
      if (activeTop !== syncData.modId) setActiveTop(syncData.modId);
      if (activeLink !== syncData.clave) setActiveLink(syncData.clave);
      if (String(activeMenuId) !== String(syncData.idMenu)) setActiveMenuId(syncData.idMenu);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, flatMenus, syncMenuWithLocation]);

  const menuDynamic = useMemo(
    () => buildSidebarTreeForModule(flatMenus, activeTop),
    [flatMenus, activeTop]
  );

  const toggleExpand = (path, currentVal) => {
    setExpandedState(prev => ({
      ...prev,
      [activeTop]: { ...(prev[activeTop] || {}), [path]: !currentVal }
    }));
  };

  const handleClickLink = useCallback((node) => {
    const trimmedClave = String(node.clave || "").trim();
    setActiveLink(trimmedClave);
    setActiveMenuId(node.id);

    if (node.hasChildren) return;

    const routeStr = String(node.route || "").toLowerCase().trim();
    if (routeStr.includes("/navigate") && trimmedClave.startsWith("/")) {
      navigate(trimmedClave);
    } else {
      const path = `${node.route}${node.clave}`;
      navigate(`/menu/dynamic?path=${encodeURIComponent(path)}`);
    }
  }, [navigate]);

  const handleChangeTab = useCallback((val) => {
    const stringVal = String(val);
    setActiveTop(stringVal);
    // Limpiamos selección al cambiar de pestaña superior para evitar resaltados cruzados
    setActiveLink("");
    setActiveMenuId(null);
  }, []);

  return (
    <div className="sk-shell">
      <SigmaTopbar
        activeTop={activeTop}
        onChangeTab={handleChangeTab}
        onToggleSidebar={() => setSidebarOpen(v => !v)}
        sidebarOpen={sidebarOpen}
        onGoHome={goHome}
        idUsuario={idUsuario}
      />
      <div className={`sk-main ${sidebarOpen ? "is-open" : "is-closed"}`}>
        <SigmaSidebar
          menu={menuDynamic}
          sidebarOpen={sidebarOpen}
          activeLink={activeLink}
          activeId={activeMenuId}
          expandedState={expandedState}
          activeTop={activeTop}
          onToggleExpand={toggleExpand}
          onClickLink={handleClickLink}
          loading={loadingMenus}
        />
        <section className={`sk-content ${sidebarOpen ? "with-sidebar" : "full"}`}>
          <Outlet key={location.pathname + location.search} />
        </section>
      </div>
    </div>
  );
};
